import { HTMLAttributes, useState, type JSX } from 'react';
import classNames from 'classnames';
import { signInWithPopup } from 'firebase/auth';
import { Box, Menu, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { auth, googleAuthProvider } from '../../config';
import { useUser } from '../../providers';
import UserAvatar from '../UserAvatar';
import classes from './UserMenu.module.css';

type TUserMenuProps = HTMLAttributes<HTMLDivElement>;

async function handleUserLogin(): Promise<void> {
  try {
    await signInWithPopup(auth, googleAuthProvider);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function UserMenu({ className, ...props }: Readonly<TUserMenuProps>): JSX.Element {
  const { user } = useUser();
  const [opened, setOpened] = useState(false);

  async function handleUserLogout(): Promise<void> {
    setOpened(false);
    try {
      await auth.signOut();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  const avatar = (
    <UserAvatar
      displayName={user?.displayName}
      photoURL={user?.photoURL}
      className={classNames({
        [classes.avatar]: !user?.photoURL,
        [classes.avatarLoggedIn]: !!user?.photoURL,
      })}
    />
  );

  function renderContent(): JSX.Element {
    if (!user) {
      return (
        <Tooltip label="Log in" position="bottom-end" withArrow>
          <UnstyledButton className={classes.avatarButton} onClick={handleUserLogin}>
            {avatar}
          </UnstyledButton>
        </Tooltip>
      );
    }

    return (
      <Menu opened={opened} onChange={setOpened} position="bottom-end" offset={8}>
        <Menu.Target>
          <UnstyledButton className={classes.avatarButton}>{avatar}</UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <div className={classes.userInfo}>
            <Text className={classes.userName}>{user.displayName}</Text>
            {user.email && <Text className={classes.userEmail}>{user.email}</Text>}
          </div>
          <Menu.Divider />
          <Menu.Item onClick={handleUserLogout}>
            <Text ta="center">Logout</Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <Box className={classNames(classes.menuContainer, className)} {...props}>
      {renderContent()}
    </Box>
  );
}

export default UserMenu;
